import React, { useState } from 'react'
import {
  Box, Button, MenuItem, CircularProgress,
  Alert, TextField, Typography, Divider,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../auth/AuthContext'

const GRADES    = [6, 7, 8, 9, 10, 11, 12]
const COUNTRIES = ['USA', 'Canada', 'United Kingdom', 'Australia', 'Other']

// Fall year: if before August, the upcoming fall is this calendar year; otherwise next year
const now       = new Date()
const FALL_YEAR = now.getMonth() < 7 ? now.getFullYear() : now.getFullYear() + 1

interface FormState {
  date_of_birth:  string
  grade:          string
  school_name:    string
  school_city:    string
  school_state:   string
  school_country: string
}

const EMPTY: FormState = {
  date_of_birth:  '',
  grade:          '',
  school_name:    '',
  school_city:    '',
  school_state:   '',
  school_country: 'USA',
}

const HIGHLIGHTS = [
  { icon: <EmojiEventsOutlinedIcon sx={{ fontSize: 18 }} />,  text: 'Guided by mentors with 16+ years of science fair wins' },
  { icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />,  text: 'AI that teaches responsible research, not shortcuts' },
  { icon: <CheckCircleOutlineIcon  sx={{ fontSize: 18 }} />,  text: 'Built around competition deadlines that matter' },
]

interface Props { onComplete: () => void }

export default function Onboarding({ onComplete }: Props) {
  const { session } = useAuth()
  const [step,   setStep]   = useState(0)
  const [form,   setForm]   = useState<FormState>(EMPTY)
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState<string | null>(null)

  const isTrial = session?.user.user_metadata?.is_trial === true

  function set(field: keyof FormState) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [field]: e.target.value }))
  }

  async function handleFinish() {
    if (!session) return
    setSaving(true)
    setError(null)

    const now = new Date().toISOString()
    const { error: insertErr } = await supabase.from('student_profile').insert({
      user_id:             session.user.id,
      date_of_birth:       form.date_of_birth,
      grade:               Number(form.grade),
      school_name:         form.school_name,
      school_city:         form.school_city,
      school_state:        form.school_state,
      school_country:      form.school_country,
      program_type:        isTrial ? 'trial' : 'ardent_enrolled',
      subscription_status: isTrial ? 'free'  : 'beta',
      lifecycle_state:     isTrial ? 'trial_active' : 'enrolled_active',
      trial_started_at:    isTrial ? now : null,
    })

    if (insertErr) { setError(insertErr.message); setSaving(false); return }

    await supabase
      .from('users')
      .update({ status: 'active', last_login: new Date().toISOString() })
      .eq('user_id', session.user.id)

    const { data: profile } = await supabase
      .from('student_profile')
      .select('student_id')
      .eq('user_id', session.user.id)
      .maybeSingle()

    if (profile) {
      // Students can't insert a project (or the initial lifecycle event) under
      // RLS — the server creates both.
      const { data: ens, error: ensErr } = await supabase.functions.invoke('ensure-project', {
        body: { student_id: profile.student_id, onboarding: true },
      })
      if (ensErr || ens?.error) {
        setError(`Your profile is saved, but we couldn't set up your project: ${ensErr?.message ?? ens.error}. Please try again.`)
        setSaving(false)
        return
      }

      // For trial students, link the parent shadow record from trial_lead.
      // Best-effort — failure here doesn't block the student from continuing.
      if (isTrial) {
        try {
          await supabase.functions.invoke('link-parent-from-trial', { body: {} })
        } catch (e) {
          console.warn('[onboarding] link-parent-from-trial failed:', e)
        }
      }
    }

    onComplete()
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>

      {/* ── Left: acceptance panel ── */}
      <Box sx={{
        display:       { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent:'space-between',
        width:         '42%',
        bgcolor:       'primary.main',
        p:             6,
      }}>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#FFFFFF', letterSpacing: '-0.01em', lineHeight: 1 }}>
            Ardent
          </Typography>
          <Typography sx={{ fontWeight: 600, fontSize: '0.6rem', color: 'secondary.main', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Scholar
          </Typography>
        </Box>

        <Box>
          {/* Acceptance badge */}
          <Box sx={{
            display:      'inline-flex',
            alignItems:   'center',
            gap:          1,
            bgcolor:      'rgba(200,151,58,0.15)',
            border:       '1px solid rgba(200,151,58,0.4)',
            borderRadius: '6px',
            px:           1.5,
            py:           0.75,
            mb:           3,
          }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'secondary.main', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {isTrial ? 'Free Trial Activated' : 'Accepted'}
            </Typography>
          </Box>

          <Typography variant="h3" sx={{ color: '#FFFFFF', fontWeight: 800, lineHeight: 1.15, mb: 1.5 }}>
            Your research<br />journey starts<br />today.
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem', lineHeight: 1.7, mb: 4 }}>
            {isTrial
              ? 'Explore the program, find your research direction, and see what original science looks like.'
              : 'You have been selected for a program that has guided students to wins at every level of science fair competition.'}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {HIGHLIGHTS.map((h, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ color: 'secondary.main', mt: 0.15 }}>{h.icon}</Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.85rem', lineHeight: 1.5 }}>
                  {h.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.72rem' }}>
          © {new Date().getFullYear()} Ardent Scholar · A program of{' '}
          <a href="https://ardentresearch.org" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
            Ardent Research
          </a>
        </Typography>
      </Box>

      {/* ── Right: form ── */}
      <Box sx={{
        flex:           1,
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        justifyContent: 'center',
        p:              { xs: 3, sm: 6 },
        bgcolor:        'background.paper',
      }}>
        {/* Mobile wordmark */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 3, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: 'primary.main' }}>Ardent</Typography>
          <Typography sx={{ fontWeight: 600, fontSize: '0.6rem', color: 'secondary.main', letterSpacing: '0.14em', textTransform: 'uppercase' }}>Scholar</Typography>
        </Box>

        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {step === 0 ? (
            <>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Let's set up your profile
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 4, fontSize: '0.9rem' }}>
                Step 1 of 2 — Personal info
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <TextField
                  label="Date of Birth"
                  type="date"
                  value={form.date_of_birth}
                  onChange={set('date_of_birth')}
                  slotProps={{ inputLabel: { shrink: true } }}
                  required fullWidth
                  helperText="Required for COPPA compliance"
                />
                <TextField
                  select
                  label={`Upcoming grade in Fall ${FALL_YEAR}`}
                  value={form.grade}
                  onChange={set('grade')}
                  required fullWidth
                >
                  {GRADES.map(g => <MenuItem key={g} value={g}>Grade {g}</MenuItem>)}
                </TextField>
                <Button
                  variant="contained"
                  disabled={!form.date_of_birth || !form.grade}
                  onClick={() => setStep(1)}
                  fullWidth
                  sx={{ py: 1.25, mt: 0.5 }}
                >
                  Continue
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Almost there
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 4, fontSize: '0.9rem' }}>
                Step 2 of 2 — Your school
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <TextField label="School Name"    value={form.school_name}  onChange={set('school_name')}  required fullWidth />
                <TextField label="City"           value={form.school_city}  onChange={set('school_city')}  required fullWidth />
                <TextField label="State / Region" value={form.school_state} onChange={set('school_state')} required fullWidth />
                <TextField select label="Country" value={form.school_country} onChange={set('school_country')} required fullWidth>
                  {COUNTRIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>

                <Divider sx={{ my: 0.5 }} />

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button variant="outlined" onClick={() => setStep(0)} fullWidth sx={{ py: 1.25 }}>Back</Button>
                  <Button
                    variant="contained"
                    disabled={!form.school_name || !form.school_city || !form.school_state || saving}
                    onClick={handleFinish}
                    fullWidth
                    sx={{ py: 1.25 }}
                  >
                    {saving ? <CircularProgress size={22} color="inherit" /> : 'Begin My Journey'}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>

    </Box>
  )
}
